// Pack-tinted confetti for the answer-correct moment. Palettes live in
// packPalette.ts so confetti and the XP fly-up share one source of truth.

import confetti from 'canvas-confetti'
import type { PackId } from '@skena/schema'
import { PACK_CONFETTI_COLORS } from './packPalette.ts'

export function fireConfetti(packId: PackId, originX = 0.5, originY = 0.55): void {
  const colors = PACK_CONFETTI_COLORS[packId]
  // Two staggered bursts — single-shot looks thin against a bright UI.
  confetti({
    particleCount: 70,
    spread: 70,
    startVelocity: 38,
    scalar: 0.9,
    ticks: 180,
    origin: { x: originX, y: originY },
    colors,
    disableForReducedMotion: true,
  })
  setTimeout(() => {
    confetti({
      particleCount: 35,
      spread: 100,
      startVelocity: 30,
      scalar: 0.8,
      ticks: 150,
      origin: { x: originX, y: originY },
      colors,
      disableForReducedMotion: true,
    })
  }, 120)
}

// Step 13 capstone moment: the rarest celebration in the app. ≥1.5× density
// of fireConfetti's first burst, with a gold-and-pack-primary palette to set
// it apart visually from per-item unlocks.
const CAPSTONE_PALETTE: Record<PackId, string[]> = {
  joseph: ['#c89a44', '#3a2818', '#a8957c', '#c9722d', '#f5d68a'],
  nativity: ['#c89a44', '#3a2818', '#a8957c', '#1f3a5f', '#f5c542'],
}

export function fireCapstoneConfetti(packId: PackId, originX = 0.5, originY = 0.5): void {
  const colors = CAPSTONE_PALETTE[packId]
  // 60 + 50 = 110 particles total. First burst alone is 60 vs fireConfetti's
  // 70-then-35 mix, and the second burst lands wider to give the panorama
  // a rippling "you did it" wash rather than a single pop.
  confetti({
    particleCount: 60,
    spread: 90,
    startVelocity: 44,
    scalar: 1.05,
    ticks: 220,
    origin: { x: originX, y: originY },
    colors,
    disableForReducedMotion: true,
  })
  setTimeout(() => {
    confetti({
      particleCount: 50,
      spread: 140,
      startVelocity: 32,
      scalar: 0.9,
      ticks: 200,
      origin: { x: originX, y: originY },
      colors,
      disableForReducedMotion: true,
    })
  }, 180)
}

// practice-loop-v2: combo tier celebrations. Particle counts are exported so
// a unit test can assert the tiers escalate strictly (spec: each tier
// visibly stronger). Index = tier (0 unused).
export const COMBO_TIER_PARTICLES = [0, 45, 75, 110] as const
/** Donkey passive: celebrations amplified. */
export const DONKEY_AMPLIFY = 1.6

export function fireComboConfetti(
  packId: PackId,
  tier: 1 | 2 | 3,
  amplified: boolean,
  originX = 0.5,
  originY = 0.4,
): void {
  const colors = PACK_CONFETTI_COLORS[packId]
  const count = Math.round(COMBO_TIER_PARTICLES[tier] * (amplified ? DONKEY_AMPLIFY : 1))
  confetti({
    particleCount: count,
    spread: 70 + tier * 20,
    startVelocity: 34 + tier * 6,
    scalar: 0.85 + tier * 0.05,
    ticks: 160 + tier * 30,
    origin: { x: originX, y: originY },
    colors,
    disableForReducedMotion: true,
  })
}
